import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';

type Status = 'queued' | 'processing' | 'completed' | 'failed';

interface UseEditorRenderProgressResult {
  progress: number;
  jobStatus: Status;
  renderVideoId: string | null;
  errorMessage: string | null;
  setRenderVideoId: (id: string | null) => void;
  setErrorMessage: (msg: string | null) => void;
}

export function useEditorRenderProgress(
  authedFetch: typeof fetch,
  activeRenderJobId: string | null,
  t: (key: string) => string
): UseEditorRenderProgressResult {
  const [progress, setProgress] = useState(0);
  const [jobStatus, setJobStatus] = useState<Status>('queued');
  const [renderVideoId, setRenderVideoId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    if (!activeRenderJobId) {
      setProgress(0);
      setJobStatus('queued');
      setErrorMessage(null);
      return;
    }
    let cancelled = false;
    let pollTimer: number | undefined;

    const handleEvent = (data: any) => {
      if (cancelled) return;
      if (data.status) setJobStatus(data.status);
      if (typeof data.progress === 'number') setProgress(data.progress);
      if (data.status === 'completed') {
        setRenderVideoId(data.videoId ?? null);
        setProgress(100);
        // Hand off to My Studio after a short pause so the user sees the
        // "Render complete" frame before the redirect.
        window.setTimeout(() => {
          router.push('/studio');
        }, 1400);
      }
      if (data.status === 'failed') {
        setErrorMessage(data.errorMessage ?? t('studio.editor.renderFailed'));
      }
    };

    const eventSource = new EventSource(`/api/editor-render-jobs/${activeRenderJobId}/stream`, {
      withCredentials: true,
    });

    eventSource.onmessage = (event) => {
      try {
        handleEvent(JSON.parse(event.data));
      } catch {
        /* heartbeat or non-JSON */
      }
    };

    eventSource.onerror = () => {
      eventSource.close();
      pollTimer = window.setInterval(async () => {
        if (cancelled) return;
        try {
          const r = await authedFetch(`/api/editor-render-jobs/${activeRenderJobId}`);
          const j = await r.json();
          if (j?.success && j.job) handleEvent(j.job);
        } catch {
          /* ignore */
        }
      }, 4000);
    };

    return () => {
      cancelled = true;
      eventSource.close();
      if (pollTimer) window.clearInterval(pollTimer);
    };
  }, [activeRenderJobId, authedFetch, t]);

  return { progress, jobStatus, renderVideoId, errorMessage, setRenderVideoId, setErrorMessage };
}
